import type { BrandLearningSourceSummary } from '@mondaygpt/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Skeleton,
} from 'components'
import { cn } from 'lib/utils'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useState } from 'react'

/**
 * "How you look" — the visual half of the brand-learning summary surface.
 * Used by both `BrandCardStep` (compact `card` variant, embedded in the
 * onboarding confirmation card) and `BrandKitPage` (full `page` variant
 * with refresh/reset CTAs).
 *
 * The auto-extracted palette and the Composition/People/Overlay chip pickers
 * were intentionally removed from the UI — they read as raw analyzer output
 * (Composition/People/Overlay use jargon; the palette is dominant pixel
 * colors that conflict with the user-defined Brand Colors). The mined data
 * still flows through the backend and is used internally to steer creative
 * generation; we just don't ask the user to interpret it.
 */
export interface BrandVisualSectionProps {
  /** Per-source summary. `null` = no data yet → skeletons. */
  summary: BrandLearningSourceSummary | null
  isLoading: boolean
  /** Accepted but currently ignored — no editable chips are rendered. */
  onOverride?: (fieldPath: string, value: unknown) => Promise<void>
  /** Accepted but currently ignored — no editable chips are rendered. */
  onClearOverride?: (fieldPath: string) => Promise<void>
  /** When provided, a "Refresh brand learning" CTA is rendered. */
  onRefresh?: () => Promise<void>
  /** When provided + variant='page', a destructive Reset action is rendered. */
  onReset?: () => Promise<void>
  /** `card` = compact for BrandCardStep, `page` = full editor for BrandKitPage. */
  variant: 'card' | 'page'
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatRefreshedAt(iso: string | null): string {
  if (!iso) return 'Never refreshed'
  const date = new Date(iso)
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'Last refreshed: just now'
  if (diffMin < 60) return `Last refreshed: ${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `Last refreshed: ${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `Last refreshed: ${diffDay}d ago`
  return `Last refreshed: ${date.toLocaleDateString()}`
}

function sourceHeader(source: BrandLearningSourceSummary | null): {
  title: string
  subtitle: string
} {
  if (!source || !source.aggregates) {
    return {
      title: 'How you look',
      subtitle: "We'll surface your top posts here once mining finishes.",
    }
  }
  if (source.source === 'instagram') {
    return {
      title: 'How you look',
      subtitle: 'Based on your last ~25 Instagram posts.',
    }
  }
  if (source.source === 'website') {
    return {
      title: 'How you look',
      subtitle: 'Based on your homepage.',
    }
  }
  return {
    title: 'How you look',
    subtitle: `Based on your ${source.source} signals.`,
  }
}

/**
 * Did the most recent mining run fail? Drives the inline destructive indicator
 * + Retry link in the section header. We only care about the last entry — older
 * failures that were superseded by a successful run shouldn't keep nagging.
 */
function latestRefreshFailed(source: BrandLearningSourceSummary | null): boolean {
  if (!source || source.refreshHistory.length === 0) return false
  const last = source.refreshHistory[source.refreshHistory.length - 1]
  return last.status === 'failed'
}

// ─── Component ──────────────────────────────────────────────────────────────

export const BrandVisualSection: React.FC<BrandVisualSectionProps> = ({
  summary,
  isLoading,
  onRefresh,
  onReset,
  variant,
}) => {
  const { title, subtitle } = sourceHeader(summary)
  const isPage = variant === 'page'
  const thumbSize = isPage ? 120 : 80

  const aggregates = summary?.aggregates ?? null
  const lastRunFailed = latestRefreshFailed(summary)

  const exemplars = summary?.exemplarMediaUrls?.slice(0, 3) ?? []
  const recurringProps = aggregates?.visual.recurringProps ?? []
  const recurringSubjects = aggregates?.visual.recurringSubjects ?? []
  const recurringSetting = aggregates?.visual.recurringSetting ?? []
  const recurringLighting = aggregates?.visual.recurringLighting ?? []

  const hasExemplars = isLoading || exemplars.length > 0

  // Card variant (onboarding): the header + placeholder copy ("How you look /
  // We'll surface your top posts here once mining finishes.") was dead UI once
  // palette + style chips were stripped. Suppress the whole section when there
  // aren't even exemplars to render; otherwise just render the thumbnail strip
  // with no header.
  if (!isPage && !hasExemplars) return null

  return (
    <section className={cn('flex flex-col gap-4', isPage && 'card p-6')}>
      {isPage && (
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">{title}</h3>
            <p className="text-[12px] leading-[1.5] text-paper-dim">{subtitle}</p>
            {lastRunFailed && <FailedRefreshNotice onRetry={onRefresh} />}
          </div>
        </header>
      )}

      {/* Exemplar thumbnails. While loading, render three skeletons so the
          layout doesn't pop. When there's nothing to show (mining hasn't
          completed yet, or returned no usable media), suppress the row
          entirely — rendering three em-dash placeholders adds visual noise
          and falsely suggests there's an interaction missing. */}
      {hasExemplars && (
        <div className="flex flex-wrap items-start gap-2">
          {isLoading && exemplars.length === 0
            ? Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="dark"
                  className="rounded-[10px]"
                  style={{ width: thumbSize, height: thumbSize }}
                />
              ))
            : exemplars.map(ex => (
                <ExemplarThumb key={ex.postId} url={ex.mediaUrl} size={thumbSize} />
              ))}
        </div>
      )}

      {/* Page-only: recurring props + reset/refresh footer */}
      {isPage && (
        <div className="flex flex-col gap-2">
          <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Recurring props
          </div>
          {recurringProps.length === 0 ? (
            <span className="text-[12px] text-paper-faint">No props detected yet.</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recurringProps.map(prop => (
                <span key={prop} className="chip" data-active="false">
                  {prop}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isPage && (
        <div className="flex flex-col gap-2">
          <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Subjects
          </div>
          {recurringSubjects.length === 0 ? (
            <span className="text-[12px] text-paper-faint">No subjects detected yet.</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recurringSubjects.map(tag => (
                <span key={tag} className="chip" data-active="false">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isPage && (
        <div className="flex flex-col gap-2">
          <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Backdrops
          </div>
          {recurringSetting.length === 0 ? (
            <span className="text-[12px] text-paper-faint">No backdrops detected yet.</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recurringSetting.map(tag => (
                <span key={tag} className="chip" data-active="false">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isPage && (
        <div className="flex flex-col gap-2">
          <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Lighting
          </div>
          {recurringLighting.length === 0 ? (
            <span className="text-[12px] text-paper-faint">No lighting detected yet.</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recurringLighting.map(tag => (
                <span key={tag} className="chip" data-active="false">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isPage && (onRefresh || onReset) && (
        <footer className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <span className="text-[11px] text-paper-faint">
            {formatRefreshedAt(summary?.lastRefreshedAt ?? null)}
          </span>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                variant="t3-tertiary"
                size="t3-sm"
                onClick={() => {
                  void onRefresh()
                }}
              >
                <RefreshCw size={14} />
                Refresh brand learning
              </Button>
            )}
            {onReset && <ResetButton onReset={onReset} />}
          </div>
        </footer>
      )}
    </section>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

const ExemplarThumb: React.FC<{ url: string; size: number }> = ({ url, size }) => (
  <div
    className="overflow-hidden rounded-[10px] border border-line-bright bg-bg-3"
    style={{ width: size, height: size }}
  >
    <img src={url} alt="Brand exemplar" className="h-full w-full object-cover" loading="lazy" />
  </div>
)

/**
 * Inline destructive indicator surfaced when the latest mining run failed.
 * The trailing "Retry" link reuses the parent-provided `onRefresh` handler so
 * the user can re-kick the same source from the same place they were already
 * looking.
 */
const FailedRefreshNotice: React.FC<{ onRetry?: () => Promise<void> }> = ({ onRetry }) => {
  const [retrying, setRetrying] = useState(false)
  return (
    <p
      className="mt-1 inline-flex flex-wrap items-center gap-1.5 text-[12px] text-[color:var(--destructive)]"
      role="status"
    >
      <AlertTriangle size={12} />
      <span>Last refresh failed.</span>
      {onRetry && (
        <button
          type="button"
          disabled={retrying}
          onClick={async () => {
            setRetrying(true)
            try {
              await onRetry()
            } finally {
              setRetrying(false)
            }
          }}
          className="underline underline-offset-2 disabled:opacity-50"
        >
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </p>
  )
}

/**
 * Destructive Reset CTA → confirmation via the shared Radix AlertDialog
 * primitive. Gets focus trap, ESC-to-cancel, and initial focus for free.
 */
const ResetButton: React.FC<{ onReset: () => Promise<void> }> = ({ onReset }) => {
  const [open, setOpen] = useState(false)
  const [resetting, setResetting] = useState(false)
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="t3-destructive" size="t3-sm" onClick={() => setOpen(true)}>
        Reset
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset brand learning?</AlertDialogTitle>
          <AlertDialogDescription>
            This will erase mined brand signals for this source. Your custom overrides will be lost.
            Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={resetting}
            onClick={async e => {
              // Prevent Radix from auto-closing before the async work resolves.
              e.preventDefault()
              setResetting(true)
              try {
                await onReset()
                setOpen(false)
              } finally {
                setResetting(false)
              }
            }}
          >
            {resetting ? 'Resetting…' : 'Yes, reset'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
